<template>
  <AdminLayout page-title="Settings" page-subtitle="Platform configuration">
    <div class="space-y-8">
      <div class="mb-4">
        <h1 class="text-3xl font-bold text-white mb-2">Settings</h1>
        <p class="text-slate-400">Platform name, access, and system alerts.</p>
      </div>

      <!-- Settings Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <!-- Modern Settings Navigation -->
        <div class="lg:col-span-1">
          <div class="settings-nav-card">
            <div class="p-6">
              <h2 class="text-xl font-bold text-white mb-6 flex items-center">
                <div class="w-8 h-8 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-lg flex items-center justify-center mr-3">
                  <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"/>
                  </svg>
                </div>
                Categories
              </h2>
              <nav class="space-y-2">
                <button
                  v-for="category in settingsCategories"
                  :key="category.id"
                  @click="activeCategory = category.id"
                  :class="[
                    'w-full flex items-center px-4 py-4 text-left text-sm font-medium rounded-xl transition-all duration-300 group',
                    activeCategory === category.id
                      ? 'bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 text-emerald-400 border border-emerald-500/30 shadow-lg shadow-emerald-500/10'
                      : 'text-slate-300 hover:bg-slate-700/50 hover:text-emerald-400 border border-transparent hover:border-slate-600/50'
                  ]"
                >
                  <div :class="[
                    'w-8 h-8 rounded-lg flex items-center justify-center mr-3 transition-all duration-300',
                    activeCategory === category.id 
                      ? 'bg-gradient-to-br from-emerald-500 to-cyan-500 shadow-lg' 
                      : 'bg-slate-700/50 group-hover:bg-slate-600/50'
                  ]">
                    <component :is="category.icon" class="w-4 h-4 text-white" />
                  </div>
                  <div class="flex-1">
                    <div class="font-semibold">{{ category.name }}</div>
                    <div class="text-xs text-slate-400 group-hover:text-slate-300">{{ category.description }}</div>
                  </div>
                </button>
              </nav>
            </div>
          </div>
        </div>

        <!-- Modern Settings Content -->
        <div class="lg:col-span-3">
          <!-- General Settings -->
          <div v-if="activeCategory === 'general'" class="settings-content-card">
            <div class="settings-header">
              <div class="flex items-center mb-4">
                <div class="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-2xl flex items-center justify-center mr-4">
                  <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  </svg>
                </div>
                <div>
                  <h2 class="text-2xl font-bold text-white">General Settings</h2>
                  <p class="text-slate-400">Basic platform configuration and branding</p>
                </div>
              </div>
            </div>
            
            <div class="settings-body">
              <div class="settings-section">
                <h3 class="settings-section-title">Platform Identity</h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="form-field">
                    <label class="form-label">Platform Name</label>
                    <input
                      v-model="settings.general.platformName"
                      type="text"
                      class="form-input"
                      placeholder="Enter platform name"
                    >
                  </div>
                  <div class="form-field">
                    <label class="form-label">Support Email</label>
                    <input
                      v-model="settings.general.supportEmail"
                      type="email"
                      class="form-input"
                      placeholder="support@example.com"
                    >
                  </div>
                </div>
                
                <div class="form-field">
                  <label class="form-label">Platform Description</label>
                  <textarea
                    v-model="settings.general.description"
                    rows="4"
                    class="form-input"
                    placeholder="Describe your platform..."
                  ></textarea>
                </div>
              </div>

              <div class="settings-section">
                <h3 class="settings-section-title">System Controls</h3>
                <div class="settings-toggle-item">
                  <div class="flex-1">
                    <h4 class="text-lg font-semibold text-white">Maintenance Mode</h4>
                    <p class="text-slate-400">Temporarily disable platform access for maintenance</p>
                    <div class="mt-2 text-sm text-slate-500">
                      Users will see a maintenance page when enabled
                    </div>
                  </div>
                  <button
                    @click="settings.general.maintenanceMode = !settings.general.maintenanceMode"
                    :class="[
                      'relative inline-flex h-8 w-14 items-center rounded-full transition-colors shadow-lg',
                      settings.general.maintenanceMode ? 'bg-emerald-600' : 'bg-slate-600'
                    ]"
                  >
                    <span
                      :class="[
                        'inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-sm',
                        settings.general.maintenanceMode ? 'translate-x-7' : 'translate-x-1'
                      ]"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Security Settings -->
          <div v-if="activeCategory === 'security'" class="settings-content-card">
            <div class="settings-header">
              <div class="flex items-center mb-4">
                <div class="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-2xl flex items-center justify-center mr-4">
                  <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                  </svg>
                </div>
                <div>
                  <h2 class="text-2xl font-bold text-white">Security Settings</h2>
                  <p class="text-slate-400">Authentication, access control, and security policies</p>
                </div>
              </div>
            </div>
            
            <div class="settings-body">
              <div class="settings-section">
                <h3 class="settings-section-title">Authentication</h3>
                <div class="settings-toggle-item">
                  <div class="flex-1">
                    <h4 class="text-lg font-semibold text-white">Two-Factor Authentication</h4>
                    <p class="text-slate-400">Require 2FA for all admin accounts</p>
                    <div class="mt-2 text-sm text-slate-500">
                      Enhances security with additional verification step
                    </div>
                  </div>
                  <button
                    @click="settings.security.requireTwoFactor = !settings.security.requireTwoFactor"
                    :class="[
                      'relative inline-flex h-8 w-14 items-center rounded-full transition-colors shadow-lg',
                      settings.security.requireTwoFactor ? 'bg-emerald-600' : 'bg-slate-600'
                    ]"
                  >
                    <span
                      :class="[
                        'inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-sm',
                        settings.security.requireTwoFactor ? 'translate-x-7' : 'translate-x-1'
                      ]"
                    />
                  </button>
                </div>
              </div>

              <div class="settings-section">
                <h3 class="settings-section-title">Session Management</h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="form-field">
                    <label class="form-label">Session Timeout (minutes)</label>
                    <input
                      v-model.number="settings.security.sessionTimeout"
                      type="number"
                      min="5"
                      max="480"
                      class="form-input"
                      placeholder="60"
                    >
                  </div>
                  <div class="form-field">
                    <label class="form-label">Max Login Attempts</label>
                    <input
                      v-model.number="settings.security.maxLoginAttempts"
                      type="number"
                      min="3"
                      max="10"
                      class="form-input"
                      placeholder="5"
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Notification Settings -->
          <div v-if="activeCategory === 'notifications'" class="settings-content-card">
            <div class="settings-header">
              <div class="flex items-center mb-4">
                <div class="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-2xl flex items-center justify-center mr-4">
                  <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-5 5v-5z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.021 4.021a1 1 0 011.414 0L12 10.586l6.565-6.565a1 1 0 111.414 1.414L13.414 12l6.565 6.565a1 1 0 01-1.414 1.414L12 13.414l-6.565 6.565a1 1 0 01-1.414-1.414L10.586 12 4.021 5.435a1 1 0 010-1.414z"/>
                  </svg>
                </div>
                <div>
                  <h2 class="text-2xl font-bold text-white">Notification Settings</h2>
                  <p class="text-slate-400">Configure system alerts and email notifications</p>
                </div>
              </div>
            </div>
            
            <div class="settings-body">
              <div class="settings-section">
                <h3 class="settings-section-title">System Notifications</h3>
                <div class="space-y-4">
                  <div v-for="notification in notificationTypes" :key="notification.id" class="settings-toggle-item">
                    <div class="flex-1">
                      <h4 class="text-lg font-semibold text-white">{{ notification.name }}</h4>
                      <p class="text-slate-400">{{ notification.description }}</p>
                    </div>
                    <button
                      @click="toggleNotification(notification.id)"
                      :class="[
                        'relative inline-flex h-8 w-14 items-center rounded-full transition-colors shadow-lg',
                        notification.enabled ? 'bg-emerald-600' : 'bg-slate-600'
                      ]"
                    >
                      <span
                        :class="[
                          'inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-sm',
                          notification.enabled ? 'translate-x-7' : 'translate-x-1'
                        ]"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Save Button -->
          <div class="flex justify-end mt-8">
            <button @click="saveSettings" class="btn-hero-primary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
              Save All Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import AdminLayout from './AdminLayout.vue'

// State
const activeCategory = ref('general')

const settingsCategories = [
  { 
    id: 'general', 
    name: 'General', 
    description: 'Basic platform settings',
    icon: 'GeneralIcon' 
  },
  { 
    id: 'security', 
    name: 'Security', 
    description: 'Authentication & access',
    icon: 'SecurityIcon' 
  },
  { 
    id: 'notifications', 
    name: 'Notifications', 
    description: 'System alerts & emails',
    icon: 'NotificationIcon' 
  }
]

const settings = ref({
  general: {
    platformName: 'OilBuddy AI',
    supportEmail: 'support@oilbuddy.ai',
    description: 'AI-powered oilfield operations platform connecting contractors and companies in North Dakota',
    maintenanceMode: false
  },
  security: {
    requireTwoFactor: false,
    sessionTimeout: 60,
    maxLoginAttempts: 5
  }
})

const notificationTypes = ref([
  {
    id: 'new_user',
    name: 'New User Registration',
    description: 'Notify when new users register',
    enabled: true
  },
  {
    id: 'job_posted',
    name: 'New Job Posted',
    description: 'Notify when new jobs are posted',
    enabled: true
  },
  {
    id: 'safety_incident',
    name: 'Safety Incidents',
    description: 'Notify about safety incidents',
    enabled: true
  },
  {
    id: 'system_alerts',
    name: 'System Alerts',
    description: 'Critical system notifications',
    enabled: true
  }
])

// Methods
const toggleNotification = (id: string) => {
  const notification = notificationTypes.value.find(n => n.id === id)
  if (notification) {
    notification.enabled = !notification.enabled
  }
}

const saveSettings = () => {
  console.log('Settings saved:', settings.value)
}

// Icon components
const GeneralIcon = {
  template: `
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
    </svg>
  `
}

const SecurityIcon = {
  template: `
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
    </svg>
  `
}

const NotificationIcon = {
  template: `
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-5 5v-5zM12.586 2.586A2 2 0 0010 4.172V6.5a.5.5 0 01-.5.5H6.172a2 2 0 00-1.414.586L2.586 9.758A2 2 0 002 11.172V13.5a.5.5 0 00.5.5H5.5a.5.5 0 01.5.5v2.828a2 2 0 00.586 1.414l2.172 2.172A2 2 0 0010.172 22H12.5a.5.5 0 00.5-.5V18.5a.5.5 0 01.5-.5h2.828a2 2 0 001.414-.586l2.172-2.172A2 2 0 0022 13.828V11.5a.5.5 0 00-.5-.5H18.5a.5.5 0 01-.5-.5V8.172a2 2 0 00-.586-1.414l-2.172-2.172z"/>
    </svg>
  `
}

// Lifecycle
onMounted(() => {
  // Load settings from API
})
</script>

<style scoped>
/* Modern Settings Layout */
.settings-nav-card {
  @apply bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-3xl overflow-hidden hover:border-emerald-500/20 transition-all duration-500;
}

.settings-content-card {
  @apply bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-3xl overflow-hidden hover:border-emerald-500/20 transition-all duration-500;
}

.settings-header {
  @apply p-8 border-b border-slate-700/50 bg-slate-800/20;
}

.settings-body {
  @apply p-8 space-y-8;
}

.settings-section {
  @apply space-y-6;
}

.settings-section-title {
  @apply text-lg font-bold text-white mb-6 pb-3 border-b border-slate-700/30;
}

/* Form Styling */
.form-field {
  @apply space-y-2;
}

.form-label {
  @apply block text-sm font-semibold text-white;
}

.form-input {
  @apply w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300;
}

.form-input:focus {
  @apply shadow-lg shadow-emerald-500/10;
}

/* Toggle Styling */
.settings-toggle-item {
  @apply flex items-center justify-between p-6 bg-slate-700/20 rounded-2xl border border-slate-600/30 hover:border-slate-500/50 transition-all duration-300;
}

/* Button Styling */
.btn-hero-primary {
  @apply px-8 py-4 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 shadow-lg shadow-emerald-500/25 inline-flex items-center;
}

/* Admin Card Overrides */
.admin-card-title {
  @apply text-white;
}

.admin-card-subtitle {
  @apply text-slate-400;
}
</style>
